import { Switch, Badge, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { Section, SettingRow } from '@vnetwork/ui/layout';
import { useToast, Alert } from '@vnetwork/ui/feedback';
import { useSetVersioning, s3ExtractError, type BucketSettings } from '@/api/s3';

interface TabProps {
  serviceId: string;
  bucket: string;
  data?: BucketSettings;
}

// Public-read bucket policy is deprecated in the console — it's managed via the
// S3 CLI now. This guide walks through the CLI equivalent.
const PUBLIC_ACCESS_DOCS_URL = 'https://docs.vnetwork.vn/docs/storage/object-storage/public-access';

/** Versioning toggle + read-only public-access (deprecated) and object-lock status. */
export function BucketGeneralTab({ serviceId, bucket, data }: TabProps) {
  const toast = useToast();
  const setVersioning = useSetVersioning(serviceId, bucket);
  const versioned = data?.versioning === 'Enabled';

  return (
    <Section
      title="General"
      description="Versioning, public access, and object-lock status for this bucket."
    >
      <Card>
        <CardHeader>
          <CardTitle>Bucket settings</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <SettingRow
            title="Versioning"
            description={`Keep multiple versions of each object. Currently ${versioned ? 'enabled' : 'suspended'}.`}
            control={
              <Switch
                checked={versioned}
                disabled={setVersioning.isPending}
                onCheckedChange={(next) =>
                  setVersioning.mutate(next, {
                    onSuccess: () => toast.success('Versioning updated'),
                    onError: (e) => toast.error(s3ExtractError(e, 'Failed to update versioning')),
                  })
                }
              />
            }
          />
          <SettingRow
            title="Public Access"
            description="Private — only authorized requests can read objects."
            control={
              // Deprecated in the console: the toggle is read-only and defaults to
              // off. Changing public-read policy is done via the S3 CLI (see Alert).
              <Switch checked={false} disabled />
            }
          >
            <Alert
              tone="warning"
              title="Public access is deprecated"
              actions={
                <a href={PUBLIC_ACCESS_DOCS_URL} target="_blank" rel="noreferrer">
                  Read the CLI guide
                </a>
              }
            >
              Managing public-read access from the console is no longer supported. Use the
              S3 CLI to set a bucket policy instead.
            </Alert>
          </SettingRow>
          <SettingRow
            title="Object Lock"
            description="Write-once-read-many retention. Can only be set when the bucket is created."
            control={
              <Badge tone={data?.object_lock ? 'success' : 'neutral'} appearance="subtle" size="sm">
                {data?.object_lock ? 'Enabled' : 'Disabled'}
              </Badge>
            }
          />
        </CardBody>
      </Card>
    </Section>
  );
}
